<template>
  <div class="h-screen flex">
    <!-- Off-canvas menu for mobile -->
    <div class="md:hidden">
      <div v-show="isOpen" class="fixed inset-0 flex z-40">
        <!--
                  Off-canvas menu overlay, show/hide based on off-canvas menu state.

                  Entering: "transition-opacity ease-linear duration-300"
                    From: "opacity-0"
                    To: "opacity-100"
                  Leaving: "transition-opacity ease-linear duration-300"
                    From: "opacity-100"
                    To: "opacity-0"
                -->
        <transition
          enter-active-class="transition-opacity ease-linear duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity ease-linear duration-300"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div class="fixed inset-0">
            <div class="absolute inset-0 bg-gray-600 opacity-75"></div>
          </div>
        </transition>
        <!--
                  Off-canvas menu, show/hide based on off-canvas menu state.

                  Entering: "transition ease-in-out duration-300 transform"
                    From: "-translate-x-full"
                    To: "translate-x-0"
                  Leaving: "transition ease-in-out duration-300 transform"
                    From: "translate-x-0"
                    To: "-translate-x-full"
                -->
        <transition
          enter-active-class="transition ease-in-out duration-300 transform"
          enter-class="-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition ease-in-out duration-300 transform"
          leave-class="translate-x-0"
          leave-to-class="translate-x-full"
        >
          <div
            v-show="isOpen"
            class="relative flex-1 flex flex-col max-w-xs w-full bg-green-800"
          >
            <div class="absolute top-0 right-0 -mr-14 p-1">
              <button
                @click="toggleMobileSidebar"
                class="flex items-center justify-center h-12 w-12 rounded-full focus:outline-none focus:bg-gray-600"
                aria-label="Close sidebar"
              >
                <icon set="outline" name="x" class="h-6 w-6 text-white" />
              </button>
            </div>
            <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div class="flex-shrink-0 flex items-center px-4">
                <img
                  class="w-auto"
                  src="@/assets/FGT_Logo_Horizontal_NoTagline_White.png"
                  alt="Workflow"
                />
              </div>
              <nav class="mt-5 px-2">
                <div v-for="item in menuItems" :key="item.name">
                  <SidebarItem v-bind:item="item" />
                </div>
              </nav>
            </div>
            <Profile />
          </div>
        </transition>

        <div class="flex-shrink-0 w-14">
          <!-- Force sidebar to shrink to fit close icon -->
        </div>
      </div>
    </div>

    <!-- Static sidebar for desktop -->
    <div class="hidden md:flex md:flex-shrink-0">
      <div class="flex flex-col w-64">
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex flex-col h-0 flex-1 bg-green-800">
          <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div class="flex items-center flex-shrink-0 px-4">
              <img
                class="w-auto"
                src="@/assets/FGT_Logo_Horizontal_NoTagline_White.png"
                alt="Workflow"
              />
            </div>

            <!-- Quick Search -->
            <div v-if="canShowQuickSearch" class="px-3 mt-5">
              <OrderNavigationHistory />
              <form v-on:submit.prevent="search">
                <label for="search" class="sr-only">Search</label>
                <div class="mt-1 flex rounded-md shadow-sm">
                  <div
                    class="relative flex items-stretch flex-grow focus-within:z-10"
                  >
                    <div
                      class="absolute inset-y-0 left-0 pl-0 flex items-center focus:outline-none outline-none"
                    >
                      <select
                        v-model="searchType"
                        aria-label="searchType"
                        class="form-select w-8 h-full border-transparent bg-transparent pl-0 focus:border-transparent focus:shadow-none"
                        @change="focusSearch"
                      >
                        <option>Order Number</option>
                        <option>Email</option>
                        <option>Phone</option>
                      </select>
                    </div>

                    <input
                      id="search"
                      ref="quickSearch"
                      class="form-input block w-full pl-8 rounded-none rounded-l-md sm:text-sm sm:leading-5 focus:border-green-100 focus:shadow-outline-green"
                      :placeholder="searchType"
                      v-model="searchValue"
                    />
                  </div>

                  <button
                    :disabled="searching"
                    class="-ml-px relative inline-flex items-center p-2 border border-gray-300 text-sm leading-5 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:text-gray-500 hover:bg-white focus:outline-none focus:shadow-outline-green focus:border-green-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                  >
                    <icon
                      set="outline"
                      name="search"
                      class="h-5 w-5 text-gray-400"
                      v-if="!searching"
                    />
                    <LoadingSpinner v-if="searching" class="text-gray-400" />
                  </button>
                </div>
                <div class="text-green-300 text-sm text-right mr-1">
                  {{ searchType }}
                </div>
              </form>
            </div>

            <nav class="mt-2 flex-1 px-2 bg-green-800">
              <div v-for="item in menuItems" :key="item.name">
                <SidebarItem v-bind:item="item" />
              </div>
            </nav>
          </div>

          <Profile />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Profile from "@/components/sidebar/Profile.vue";
import SidebarItem from "@/components/sidebar/SidebarItem.vue";
import OrderNavigationHistory from "@/components/sidebar/OrderNavigationHistory.vue";
import auth from "@/api/auth";

export default {
  name: "Sidebar",
  data() {
    return {
      searchType: "Order Number",
      searchValue: null,
      searching: false,
    };
  },
  components: {
    Profile,
    SidebarItem,
    OrderNavigationHistory,
  },
  computed: {
    isOpen() {
      return this.$store.getters.isOpenMobileSidebar;
    },
    canShowQuickSearch() {
      return !auth.denyFor(["bulk-inventory"]);
    },
    menuItems() {
      // Dont display the menu until we have the user groups
      if (this.$store.getters.userGroups == undefined) return null;

      // get all of the authorized nav routes
      return this.$router.options.routes
        .filter((route) => this.canShowMenuEntry(route.meta, route))
        .map((route) => {
          // get all of the authorized children nav routes
          if (route?.children?.length) {
            route.children =
              route.children?.filter((child) =>
                this.canShowMenuEntry(child.meta, child)
              ) || [];
          }

          return route;
        });
    },
  },
  methods: {
    // Take both routerMeta and route parameters for now so that we don't possibly ruin previous code paths
    // Even though routerMeta is just equal to route.meta
    canShowMenuEntry(routerMeta) {
      if (!routerMeta?.navShow) {
        return false;
      }

      if (routerMeta?.showFor?.length) {
        return auth.isUserAuthorized(routerMeta.showFor);
      }

      if (auth.denyFor(routerMeta?.hideFor || [])) {
        return false;
      }

      return true;
    },
    toggleMobileSidebar() {
      this.$store.commit("toggleMobileSidebar");
    },
    resetSearch() {
      this.searching = false;
      this.searchValue = null;
      this.$refs.quickSearch.blur();
    },
    focusSearch() {
      this.$refs.quickSearch.focus();
    },
    route(name, params = {}) {
      // avoid redundant router push if already on the current route
      if (this.$route.name !== name)
        this.$router.push({ name: name, params: params });
    },
    search() {
      this.searching = true;

      // search mapping
      const searchMap = {
        "Order Number": "SourceOrderNumber",
        Email: "Email",
        Phone: "PhoneIndex",
      };

      this.$store.commit("setQuickSearchType", this.searchType);

      switch (searchMap[this.searchType]) {
        case "CustomerID":
          // search for ONE customer and go to details page, if one not found show no customer loaded
          this.$store
            .dispatch("customers/fetchCustomer", this.searchValue)
            .then(() => {
              this.route("Details");
            })
            .finally(() => {
              this.resetSearch();
            });
          break;
        case "PhoneIndex":
        case "Email":
          // search for MANY customers based on filters and go to Search view with results
          this.$store
            .dispatch("customers/fetchCustomers", {
              [searchMap[this.searchType]]: this.searchValue,
              newSearch: true,
            })
            .then(() => {
              if (this.$route.name !== "Search") {
                this.route("Search", { searchType: this.searchType });
              }
            })
            .finally(() => {
              this.resetSearch();
            });
          break;
        case "SourceOrderNumber":
          // search for (possibly) MANY orders based on Web Order #, if only ONE result found go to order details page
          this.$store
            .dispatch("orders/fetchOrders", {
              [searchMap[this.searchType]]: this.searchValue,
              newSearch: true,
            })
            .then(async () => {
              // If the is only a single result from the search load that order and redirect to details page
              let orders = this.$store.getters["orders/getOrders"];
              if (orders.total == 1) {
              // was getting race conditioned with the redirect causing overwrites on properties
                await this.$store.dispatch(
                  "orders/fetchOrder",
                  orders.data[0].SourceOrderNumber
                );
                this.$router
                  .push("/customers/orders/" + orders.data[0].SourceOrderNumber)
                  .catch(() => {});
              } else {
                this.route("Search");
              }
            })
            .finally(() => {
              this.resetSearch();
            });
          break;
        default:
          // do nothing
          console.log("default");
          break;
      }
    },
  },
};
</script>

<style></style>
